import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, message, Table, Tooltip, Typography, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { MCPResearchContext } from '../MCPResearchUI';
import { fetchMCPServerTools } from '../MCPServerApis';
import { parseToolsCsvFile } from '../ToolsHelper';
import ToolsImportModal from './ToolsImportModal';

const { Text } = Typography;

const flagColumn = (title, dataIndex) => ({
    title,
    dataIndex,
    width: 80,
    render: (value) => (value === 1 || value === '1' ? <Text type='success'>Yes</Text> : <Text>No</Text>),
});

// DB stores parameters as a JSON array string; display as comma-separated.
const formatParameters = (value) => {
    if (!value) return '';
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
    } catch {
        return String(value);
    }
};

const Tools = () => {
    const { id } = useContext(MCPResearchContext);
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [importRows, setImportRows] = useState([]);

    const loadTools = async () => {
        setLoading(true);
        try {
            const tools = await fetchMCPServerTools(id);
            setTools(tools);
        } catch (error) {
            console.error('Error fetching MCP tools:', error);
            message.error('Failed to load MCP tools');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTools();
    }, [id]);

    const handleFileSelected = async (file) => {
        const { rows, fileErrors } = await parseToolsCsvFile(file);
        if (fileErrors.length > 0) {
            message.error(fileErrors[0]);
            return false;
        }
        setImportRows(rows);
        setModalOpen(true);
        return false;
    };

    const downloadSampleCsv = () => {
        const csv = [
            'tool_name,description,parameters,supports_read,supports_write,supports_delete',
            'sample_search_tool,Search documents,"query,limit",Yes,No,No',
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'mcp_tools_sample.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const columns = [
        { title: 'Tool Name', dataIndex: 'tool_name', width: 200 },
        {
            title: 'Description',
            dataIndex: 'description',
            ellipsis: { showTitle: false },
            render: (value) => (
                <Tooltip title={value} placement='topLeft'>
                    {value}
                </Tooltip>
            ),
        },
        {
            title: 'Parameters',
            dataIndex: 'parameters',
            ellipsis: { showTitle: false },
            render: (value) => (
                <Tooltip title={formatParameters(value)} placement='topLeft'>
                    {formatParameters(value)}
                </Tooltip>
            ),
        },
        flagColumn('Read', 'supports_read'),
        flagColumn('Write', 'supports_write'),
        flagColumn('Delete', 'supports_delete'),
    ];

    return (
        <div>
            <Card
                title='MCP Tools'
                extra={
                    <div>
                        <Tooltip title='Download a sample CSV for bulk tool import'>
                            <Button
                                size='small'
                                icon={<DownloadOutlined />}
                                style={{ marginRight: 16 }}
                                onClick={downloadSampleCsv}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        <Upload accept='.csv' showUploadList={false} beforeUpload={handleFileSelected}>
                            <Tooltip title='Import tools from a CSV file'>
                                <span>
                                    <UploadOutlined /> Import CSV
                                </span>
                            </Tooltip>
                        </Upload>
                    </div>
                }
            >
                <Table
                    rowKey='id'
                    size='small'
                    bordered
                    dataSource={tools}
                    columns={columns}
                    loading={loading}
                    pagination={{ pageSize: 25, showSizeChanger: false }}
                />
            </Card>
            <ToolsImportModal
                open={modalOpen}
                serverId={id}
                rows={importRows}
                onClose={() => setModalOpen(false)}
                onSuccess={loadTools}
            />
        </div>
    );
};

export default Tools;
